import React, { useEffect, useRef, useState } from "react";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { aiConfig } from "../..";

export interface Message {
    role?: "user" | "assistant" | string;
    content: string;
    sender?: string;
    type?: string;
    source?: string;
    isAssistant?: boolean;
}

interface AzureVoiceAssistProps {
    sendMessage: (text: string) => void;
    messages: Message[];
    buttonEnabledVoice: boolean;
    checkboxEnabledVoice: boolean;
    recognitionLanguage?: string;
    voiceName?: string;
    speakingRate?: number;
    speechTokenEndpoint?: string;
    fetchAzureSpeechToken?: () => Promise<{
        token: string;
        region: string;
        expiresInMinutes: number;
    }>;
}

export const AzureVoiceAssist: React.FC<AzureVoiceAssistProps> = ({
    sendMessage,
    messages,
    buttonEnabledVoice,
    checkboxEnabledVoice,
    recognitionLanguage = "en-US",
    voiceName = "en-US-JennyNeural",
    speakingRate = 1.0,
    speechTokenEndpoint = "/api/get-speech-token",
    fetchAzureSpeechToken,
}) => {
    // ------------------------------
    // State & refs
    // ------------------------------
    const [stopVoice, setStopVoice] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [micStatus, setMicStatus] = useState<
        "granted" | "denied" | "prompt" | "unavailable"
    >("unavailable");

    // Azure Speech SDK refs
    const speechConfigRef = useRef<SpeechSDK.SpeechConfig | null>(null);
    const recognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null);
    const synthesizerRef = useRef<SpeechSDK.SpeechSynthesizer | null>(null);
    const synthesizerClosingRef = useRef(false); // Track if we're closing to prevent double-close
    const activeAudioRef = useRef<HTMLAudioElement | null>(null); // Track currently playing audio for immediate stop

    // Robustness helpers (similar to VoiceAssist)
    const backoffRef = useRef(0);
    const lastStartTsRef = useRef(0);
    const rearmTimerRef = useRef<number | null>(null);
    const ttsQueueCountRef = useRef(0);
    const ttsQueueRef = useRef<string[]>([]);
    const ttsProcessingRef = useRef(false);
    const lastAssistantUtteranceRef = useRef<string>("");
    const noEchoUntilTsRef = useRef<number>(0);

    // Refs to track current voice enable state (avoid closure issues in event handlers)
    const buttonEnabledVoiceRef = useRef(buttonEnabledVoice);
    const checkboxEnabledVoiceRef = useRef(checkboxEnabledVoice);
    const abortTTSRef = useRef(false); // Flag to abort ongoing TTS

    // Cached speech token
    const cachedAuthRef = useRef<{
        token: string;
        region: string;
        expiryTs: number;
    } | null>(null);

    // ------------------------------
    // Sync refs with props (for event handlers)
    // ------------------------------
    useEffect(() => {
        buttonEnabledVoiceRef.current = buttonEnabledVoice;
        checkboxEnabledVoiceRef.current = checkboxEnabledVoice;
    }, [buttonEnabledVoice, checkboxEnabledVoice]);

    // ------------------------------
    // Utility functions
    // ------------------------------
    const areClose = (a: string, b: string) => {
        const tok = (s: string) => s.toLowerCase().split(/\s+/).filter(Boolean);
        const A = tok(a), B = tok(b);
        if (A.length < 3 || B.length < 3) return false;
        const setA = new Set(A), setB = new Set(B);
        const inter = [...setA].filter(x => setB.has(x)).length;
        const union = new Set([...setA, ...setB]).size;
        const jaccard = inter / union;
        return jaccard >= 0.7 || a.includes(b) || b.includes(a);
    };

    const safeToStartASR = () => {
        const isSecure = window.isSecureContext ||
            location.hostname === "localhost" ||
            location.hostname === "127.0.0.1";
        if (!isSecure) return false;
        if (document.hidden) return false;
        if (typeof navigator.onLine === "boolean" && !navigator.onLine) return false;
        if (isSpeaking || ttsQueueCountRef.current > 0) return false;
        return true;
    };

    // ------------------------------
    // Token management
    // ------------------------------
    async function getSpeechToken(): Promise<{
        token: string;
        region: string;
        expiresInMinutes: number;
    }> {
        const r = await fetch(speechTokenEndpoint);
        if (!r.ok) {
            const text = await r.text().catch(() => "");
            throw new Error(`Failed to fetch Speech token (${r.status}): ${text}`);
        }
        return await r.json();
    }

    async function ensureFreshToken() {
        const now = Date.now();
        if (cachedAuthRef.current && now < cachedAuthRef.current.expiryTs - 90_000) {
            return cachedAuthRef.current;
        }

        const { token, region, expiresInMinutes } = await getSpeechToken();
        console.log("[VA][Speech Token] fetched new token");

        cachedAuthRef.current = {
            token,
            region,
            expiryTs: Date.now() + (expiresInMinutes * 60_000 || 9 * 60_000),
        };
        return cachedAuthRef.current;
    }

    // ------------------------------
    // Rearm scheduling (similar to VoiceAssist)
    // ------------------------------
    const clearRearmTimer = () => {
        if (rearmTimerRef.current) {
            window.clearTimeout(rearmTimerRef.current);
            rearmTimerRef.current = null;
        }
    };

    const scheduleRearm = (reason: string, delayMs: number) => {
        clearRearmTimer();
        rearmTimerRef.current = window.setTimeout(() => {
            const now = Date.now();
            if (now - lastStartTsRef.current < 300) return; // throttle

            // Check if voice is enabled
            if (!buttonEnabledVoice || !checkboxEnabledVoice) {
                console.log(`[VA] Skipping rearm(${reason}) - voice disabled`);
                return;
            }

            if (!safeToStartASR()) {
                scheduleRearm("not-safe", 500);
                return;
            }
            if (stopVoice || isSpeaking || isListening) return;

            console.log(`[VA] startListening(${reason})`);
            lastStartTsRef.current = now;
            startListening();
        }, delayMs) as unknown as number;
    };

    // ------------------------------
    // Azure STT control
    // ------------------------------
    const stopListening = async () => {
        console.log("[VA][STT] Stopping recognition");
        setIsListening(false);

        if (recognizerRef.current) {
            const recognizer = recognizerRef.current;
            recognizerRef.current = null;

            try {
                // Stop continuous recognition
                await new Promise<void>((resolve) => {
                    recognizer.stopContinuousRecognitionAsync(
                        () => {
                            console.log("[VA][STT] Recognition stopped");
                            resolve();
                        },
                        (err) => {
                            console.warn("[VA][STT] Stop error:", err);
                            resolve(); // resolve anyway to continue cleanup
                        }
                    );
                });

                // Close the recognizer to release microphone
                recognizer.close();
                console.log("[VA][STT] Recognizer closed and disposed");
            } catch (error) {
                console.warn("[VA][STT] Cleanup error:", error);
            }
        }
    };

    const startListening = async () => {
        if (stopVoice || isSpeaking || isListening) return;
        if (!buttonEnabledVoice || !checkboxEnabledVoice) {
            console.log("[VA][STT] Skipping - voice disabled");
            return;
        }

        try {
            // Double-check before async operations
            if (!buttonEnabledVoice || !checkboxEnabledVoice) {
                console.log("[VA][STT] Voice disabled during start - aborting");
                return;
            }

            const auth = await ensureFreshToken();

            // Triple-check after token fetch (async race condition)
            if (!buttonEnabledVoice || !checkboxEnabledVoice) {
                console.log("[VA][STT] Voice disabled after token fetch - aborting");
                return;
            }

            // Create fresh speech config if needed
            if (!speechConfigRef.current) {
                speechConfigRef.current = SpeechSDK.SpeechConfig.fromAuthorizationToken(
                    auth.token,
                    auth.region
                );
                speechConfigRef.current.speechRecognitionLanguage = recognitionLanguage;
                speechConfigRef.current.setServiceProperty(
                    "InitialSilenceTimeoutMs",
                    "5000",
                    SpeechSDK.ServicePropertyChannel.UriQueryParameter
                );
                speechConfigRef.current.setServiceProperty(
                    "EndSilenceTimeoutMs",
                    "2000",
                    SpeechSDK.ServicePropertyChannel.UriQueryParameter
                );
            } else {
                // Refresh token
                speechConfigRef.current.authorizationToken = auth.token;
            }

            // Clean up old recognizer
            if (recognizerRef.current) {
                try {
                    recognizerRef.current.close();
                } catch { }
                recognizerRef.current = null;
            }

            // Create new recognizer
            const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
            const recognizer = new SpeechSDK.SpeechRecognizer(speechConfigRef.current, audioConfig);
            recognizerRef.current = recognizer;

            // Event handlers
            recognizer.recognized = async (sender, e) => {
                const text = (e.result?.text || "").trim();
                const reason = e.result.reason;

                if (reason === SpeechSDK.ResultReason.RecognizedSpeech && text) {
                    console.log("[VA][STT] Recognition result:", text);

                    // Check if voice is still enabled (stop recognition if disabled)
                    if (!buttonEnabledVoice || !checkboxEnabledVoice) {
                        console.log("[VA][STT] Ignoring - voice disabled, stopping recognizer");
                        await stopListening();
                        return;
                    }

                    // Skip if speaking or in cooldown
                    if (isSpeaking || ttsQueueCountRef.current > 0) {
                        console.log("[VA][STT] Ignoring - TTS active");
                        return;
                    }

                    // Echo prevention
                    if (Date.now() < noEchoUntilTsRef.current) {
                        console.log("[VA][STT] Ignoring - echo cooldown");
                        return;
                    }

                    const lastSpoken = lastAssistantUtteranceRef.current;
                    if (lastSpoken && areClose(text, lastSpoken)) {
                        console.log("[VA][STT] Ignoring - echo detected");
                        return;
                    }

                    // Voice stop commands (more specific)
                    const lower = text.toLowerCase().trim();
                    if (/^(stop|pause|quiet|silence)$/i.test(lower)) {
                        console.log("[VA][STT] Stop command detected");
                        await hardStopTTS();
                        scheduleRearm("stop-cmd", 200);
                        return;
                    }

                    // Process user input
                    console.log("[VA][STT] Processing:", text);
                    try {
                        const corrected = await fetchCorrectCity(text);
                        sendMessage(corrected || text);
                    } catch {
                        sendMessage(text);
                    }

                    // Stop and rearm
                    await stopListening();
                    scheduleRearm("user-input", 800);
                }
            };

            recognizer.canceled = (sender, e) => {
                console.log("[VA][STT] Canceled:", e.reason);
                if (!buttonEnabledVoiceRef.current || !checkboxEnabledVoiceRef.current) {
                    console.log("[VA][STT] Not rearming - voice disabled");
                    return;
                }
                backoffRef.current = Math.min(backoffRef.current + 1, 4);
                const delay = 300 + backoffRef.current * 200;
                scheduleRearm("canceled", delay);
            };

            recognizer.sessionStopped = () => {
                console.log("[VA][STT] Session stopped");
                if (!buttonEnabledVoiceRef.current || !checkboxEnabledVoiceRef.current) {
                    console.log("[VA][STT] Not rearming - voice disabled");
                    return;
                }
                scheduleRearm("session-stopped", 200);
            };

            // Start recognition
            await new Promise<void>((resolve, reject) => {
                recognizer.startContinuousRecognitionAsync(
                    () => resolve(),
                    (err) => reject(err)
                );
            });

            setIsListening(true);
            backoffRef.current = 0;
            console.log("[VA][STT] Started listening");

        } catch (error) {
            console.warn("[VA][STT] Start error:", error);
            scheduleRearm("start-error", 1000);
        }
    };

    // ------------------------------
    // Azure TTS (queue-friendly like VoiceAssist)
    // ------------------------------
    const speakFunction = async (text: string) => {
        if (!text?.trim()) return;
        if (!buttonEnabledVoiceRef.current || !checkboxEnabledVoiceRef.current) {
            console.log("[VA][TTS] Skipping - voice disabled");
            return;
        }

        console.log("[VA][TTS] Queueing text:", text.substring(0, 50) + "...");

        // Add to queue
        ttsQueueRef.current.push(text.trim());
        ttsQueueCountRef.current++;

        // Start processing if not already processing
        if (!ttsProcessingRef.current) {
            processTTSQueue().catch(error => {
                console.error("[VA][TTS] Queue processing failed:", error);
                ttsProcessingRef.current = false;
            });
        }
    };

    // Process TTS queue sequentially
    const processTTSQueue = async () => {
        if (ttsProcessingRef.current || ttsQueueRef.current.length === 0) return;
        if (!buttonEnabledVoiceRef.current || !checkboxEnabledVoiceRef.current) {
            console.log("[VA][TTS] Queue processing stopped - voice disabled");
            ttsQueueRef.current = [];
            ttsQueueCountRef.current = 0;
            return;
        }

        console.log("[VA][TTS] Starting queue processing, items:", ttsQueueRef.current.length);
        ttsProcessingRef.current = true;

        try {
            while (ttsQueueRef.current.length > 0) {
                // Check again in case voice was disabled during processing
                if (!buttonEnabledVoiceRef.current || !checkboxEnabledVoiceRef.current) {
                    console.log("[VA][TTS] Queue processing interrupted - voice disabled");
                    ttsQueueRef.current = [];
                    ttsQueueCountRef.current = 0;
                    break;
                }

                const text = ttsQueueRef.current.shift();
                if (!text) break;

                console.log("[VA][TTS] Processing from queue:", text.substring(0, 50) + "...");
                lastAssistantUtteranceRef.current = text;

                try {
                    await synthesizeText(text);
                } catch (error) {
                    console.error("[VA][TTS] Queue processing error:", error);
                    // Continue with next item instead of breaking
                }
            }
        } finally {
            ttsProcessingRef.current = false;
            console.log("[VA][TTS] Queue processing completed");
        }
    };

    // Single TTS synthesis
    // APPROACH: Use null audio config to prevent SDK auto-playback, then manually play audio through HTML Audio element we can control
    // This ensures: 1) Only ONE audio stream plays 2) We can stop it immediately when voice disabled 3) Queue items play sequentially
    const synthesizeText = async (text: string): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            if (!buttonEnabledVoiceRef.current || !checkboxEnabledVoiceRef.current) {
                console.log("[VA][TTS] Synthesis blocked - voice disabled");
                ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);
                setIsSpeaking(false);
                resolve();
                return;
            }

            console.log("[VA][TTS] Starting synthesis for:", text.substring(0, 50) + "...");

            try {
                // Double-check voice is still enabled before getting token
                if (!buttonEnabledVoiceRef.current || !checkboxEnabledVoiceRef.current) {
                    ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);
                    setIsSpeaking(false);
                    resolve();
                    return;
                }

                const auth = await ensureFreshToken();
                console.log("[VA][TTS] Got auth token");

                const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
                    auth.token,
                    auth.region
                );
                speechConfig.speechSynthesisVoiceName = voiceName;
                speechConfig.speechSynthesisOutputFormat = SpeechSDK.SpeechSynthesisOutputFormat.Audio16Khz32KBitRateMonoMp3;

                // CRITICAL: Use null audio config to prevent SDK from auto-playing
                // This ensures we only play through our controlled HTML Audio element
                const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig, null as any);

                // Final check before synthesis starts
                if (!buttonEnabledVoiceRef.current || !checkboxEnabledVoiceRef.current) {
                    console.log("[VA][TTS] Voice disabled before synthesis - aborting");
                    synthesizer.close();
                    ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);
                    setIsSpeaking(false);
                    resolve();
                    return;
                }

                // Track current synthesizer for stopping
                synthesizerRef.current = synthesizer;

                // Stop listening during TTS
                stopListening();

                // Set speaking state immediately
                setIsSpeaking(true);

                // Start synthesis with manual audio playback control
                synthesizer.speakTextAsync(
                    text,
                    (result) => {
                        console.log("[VA][TTS] Synthesis completed:", result.reason);

                        // Check if aborted BEFORE playing audio
                        const wasAborted = abortTTSRef.current || !buttonEnabledVoiceRef.current || !checkboxEnabledVoiceRef.current;

                        if (wasAborted) {
                            console.log("[VA][TTS] Completion ignored - voice was disabled");
                            setIsSpeaking(false);
                            ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);

                            if (synthesizerRef.current === synthesizer) {
                                synthesizerRef.current = null;
                            }

                            // Close synthesizer
                            setTimeout(() => {
                                if (!synthesizerClosingRef.current) {
                                    synthesizerClosingRef.current = true;
                                    try { synthesizer.close(); } catch (e) { }
                                    finally { synthesizerClosingRef.current = false; }
                                }
                            }, 100);
                            resolve(); // Resolve promise to continue queue
                            return;
                        }

                        // Get audio data from result
                        if (result.audioData) {
                            try {
                                // Convert audio data to blob URL
                                const audioBlob = new Blob([result.audioData], { type: 'audio/mp3' });
                                const audioUrl = URL.createObjectURL(audioBlob);

                                // Create and play audio element
                                const audio = new Audio(audioUrl);
                                activeAudioRef.current = audio;

                                // Handle audio end - RESOLVE PROMISE HERE
                                audio.onended = () => {
                                    console.log("[VA][TTS] Audio playback ended");
                                    URL.revokeObjectURL(audioUrl);
                                    if (activeAudioRef.current === audio) {
                                        activeAudioRef.current = null;
                                    }

                                    // Use timeout to ensure state updates properly
                                    setTimeout(() => {
                                        setIsSpeaking(false);
                                        ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);
                                        noEchoUntilTsRef.current = Date.now() + 1500;

                                        // Rearm only if voice is still enabled
                                        if (!stopVoice &&
                                            buttonEnabledVoiceRef.current &&
                                            checkboxEnabledVoiceRef.current &&
                                            ttsQueueCountRef.current === 0 &&
                                            ttsQueueRef.current.length === 0) {
                                            scheduleRearm("tts-end", 800);
                                        }

                                        // RESOLVE PROMISE - allows next queue item to play
                                        resolve();
                                    }, 50);
                                };

                                // Handle audio errors
                                audio.onerror = (e) => {
                                    console.error("[VA][TTS] Audio playback error:", e);
                                    URL.revokeObjectURL(audioUrl);
                                    if (activeAudioRef.current === audio) {
                                        activeAudioRef.current = null;
                                    }
                                    setTimeout(() => {
                                        setIsSpeaking(false);
                                        ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);
                                        resolve(); // Continue queue
                                    }, 50);
                                };

                                // Play audio ONLY if voice is still enabled
                                // Double-check here to prevent playing if disabled during synthesis
                                if (!buttonEnabledVoiceRef.current || !checkboxEnabledVoiceRef.current || abortTTSRef.current) {
                                    console.log("[VA][TTS] Voice disabled before play - canceling audio");
                                    URL.revokeObjectURL(audioUrl);
                                    activeAudioRef.current = null;
                                    setTimeout(() => {
                                        setIsSpeaking(false);
                                        ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);
                                        resolve(); // Continue queue
                                    }, 50);
                                    return;
                                }

                                console.log("[VA][TTS] Starting audio playback");
                                audio.play().catch(err => {
                                    console.error("[VA][TTS] Play error:", err);
                                    URL.revokeObjectURL(audioUrl);
                                    activeAudioRef.current = null;
                                    setTimeout(() => {
                                        setIsSpeaking(false);
                                        ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);
                                        resolve(); // Continue queue
                                    }, 50);
                                });

                            } catch (audioError) {
                                console.error("[VA][TTS] Audio creation error:", audioError);
                                setTimeout(() => {
                                    setIsSpeaking(false);
                                    ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);
                                    resolve(); // Continue queue
                                }, 50);
                            }
                        } else {
                            console.warn("[VA][TTS] No audio data in result");
                            setTimeout(() => {
                                setIsSpeaking(false);
                                ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);
                                resolve(); // Continue queue
                            }, 50);
                        }

                        // Clear synthesizer ref
                        if (synthesizerRef.current === synthesizer) {
                            synthesizerRef.current = null;
                        }

                        // Close synthesizer after callback completes
                        setTimeout(() => {
                            if (!synthesizerClosingRef.current) {
                                synthesizerClosingRef.current = true;
                                try { synthesizer.close(); } catch (e) { }
                                finally { synthesizerClosingRef.current = false; }
                            }
                        }, 100);
                    },
                    (error) => {
                        console.error("[VA][TTS] Synthesis error:", error);

                        setIsSpeaking(false);
                        ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);

                        if (synthesizerRef.current === synthesizer) {
                            synthesizerRef.current = null;
                        }

                        setTimeout(() => {
                            if (!synthesizerClosingRef.current) {
                                synthesizerClosingRef.current = true;
                                try { synthesizer.close(); } catch { }
                                finally { synthesizerClosingRef.current = false; }
                            }
                            resolve(); // Continue queue even on error
                        }, 100);
                    }
                );

            } catch (error) {
                console.error("[VA][TTS] Setup error:", error);
                setIsSpeaking(false);
                ttsQueueCountRef.current = Math.max(0, ttsQueueCountRef.current - 1);
                resolve(); // Continue queue even on setup error
            }
        });
    };

    // Hard stop TTS
    // CRITICAL: Stop the HTML Audio element immediately for instant audio stop
    const hardStopTTS = async () => {
        console.log("[VA][TTS] Hard stop requested");

        // Set abort flag FIRST - this prevents callbacks from rearming
        abortTTSRef.current = true;

        try {
            // IMMEDIATELY stop any playing audio
            if (activeAudioRef.current) {
                try {
                    activeAudioRef.current.pause();
                    activeAudioRef.current.currentTime = 0;
                    activeAudioRef.current = null;
                    console.log("[VA][TTS] Audio stopped immediately");
                } catch (audioError) {
                    console.warn("[VA][TTS] Audio stop error:", audioError);
                }
            }

            // Clear synthesizer reference (don't close - let callbacks handle it)
            if (synthesizerRef.current) {
                console.log("[VA][TTS] Clearing synthesizer ref");
                synthesizerRef.current = null;
            }

            // Reset all state immediately for UI responsiveness
            setIsSpeaking(false);
            ttsQueueCountRef.current = 0;
            ttsQueueRef.current = [];
            ttsProcessingRef.current = false;

        } catch (error) {
            console.warn("[VA][TTS] Hard stop error:", error);
        } finally {
            // Reset abort flag after callbacks have time to complete
            setTimeout(() => {
                abortTTSRef.current = false;
            }, 1000);
        }
    };

    // ------------------------------
    // Initialization
    // ------------------------------
    useEffect(() => {
        let mounted = true;

        const init = async () => {
            // Check microphone permission
            try {
                if (navigator.permissions) {
                    const p = await navigator.permissions.query({
                        name: "microphone" as PermissionName,
                    });
                    if (!mounted) return;
                    setMicStatus(p.state as any);
                    p.onchange = () => mounted && setMicStatus(p.state as any);
                } else {
                    await navigator.mediaDevices.getUserMedia({ audio: true });
                    if (!mounted) return;
                    setMicStatus("granted");
                }
            } catch {
                setMicStatus("denied");
            }

            // Prime token cache
            try {
                await ensureFreshToken();
            } catch { }
        };

        init();

        // Keep tokens fresh
        const tokenRefreshInterval = setInterval(() => {
            ensureFreshToken().catch(() => { });
        }, 8 * 60_000);

        // Cleanup on page unload/navigation
        const handleBeforeUnload = () => {
            try {
                recognizerRef.current?.close();
                synthesizerRef.current?.close();
            } catch { }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Cleanup on network changes
        const handleOffline = () => {
            console.log("[VA] Network offline - stopping voice");
            stopListening().catch(() => { });
            hardStopTTS().catch(() => { });
        };
        window.addEventListener('offline', handleOffline);

        return () => {
            mounted = false;
            clearInterval(tokenRefreshInterval);
            clearRearmTimer();
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('offline', handleOffline);

            // Clean up Azure resources
            try {
                if (recognizerRef.current) {
                    recognizerRef.current.close();
                    recognizerRef.current = null;
                }
                if (synthesizerRef.current) {
                    synthesizerRef.current.close();
                    synthesizerRef.current = null;
                }
            } catch { }
        };
    }, []);

    // ------------------------------
    // UI toggle handling
    // ------------------------------
    useEffect(() => {
        console.log("[VA] UI toggle changed:", { buttonEnabledVoice, checkboxEnabledVoice });
        if (!buttonEnabledVoice || !checkboxEnabledVoice) {
            console.log("[VA] Voice disabled via UI - stopping all operations immediately");

            // Set stop flag first
            setStopVoice(true);
            clearRearmTimer();

            // Clear TTS queue immediately
            ttsQueueRef.current = [];
            ttsQueueCountRef.current = 0;
            ttsProcessingRef.current = false;

            // Stop all voice operations (these are async but start immediately)
            stopListening().catch(err => console.warn("[VA] Error stopping STT:", err));
            hardStopTTS().catch(err => console.warn("[VA] Error stopping TTS:", err));

            return;
        }

        console.log("[VA] Voice enabled via UI");
        setStopVoice(false);
        abortTTSRef.current = false; // Reset abort flag
        scheduleRearm("ui-enable", 100);
    }, [buttonEnabledVoice, checkboxEnabledVoice]);

    // ------------------------------
    // Auto-speak assistant messages
    // ------------------------------
    useEffect(() => {
        if (!messages?.length || stopVoice) return;

        const last = messages[messages.length - 1];
        const role = (
            last?.role ||
            last?.sender ||
            last?.type ||
            last?.source ||
            ""
        ).toString().toLowerCase();

        const isAssistant =
            role === "assistant" ||
            role === "bot" ||
            last?.isAssistant === true;

        if (isAssistant && last?.content) {
            console.log("[VA][TTS] Queuing assistant message");
            speakFunction(last.content);
        }
    }, [messages, stopVoice]);

    // ------------------------------
    // Watchdog: restart if not listening and should be
    // ------------------------------
    useEffect(() => {
        if (stopVoice || isSpeaking || ttsQueueCountRef.current > 0) return;
        if (!buttonEnabledVoice || !checkboxEnabledVoice) return; // Don't rearm if voice disabled
        if (!isListening) {
            scheduleRearm("watchdog", 1000);
        }
    }, [isListening, stopVoice, isSpeaking, buttonEnabledVoice, checkboxEnabledVoice]);

    // ------------------------------
    // Visibility change handling
    // ------------------------------
    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === "visible" &&
                !stopVoice && !isSpeaking && !isListening &&
                buttonEnabledVoice && checkboxEnabledVoice) {
                scheduleRearm("visibility", 500);
            }
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [stopVoice, isSpeaking, isListening, buttonEnabledVoice, checkboxEnabledVoice]);

    // ------------------------------
    // City correction function
    // ------------------------------
    async function fetchCorrectCity(text: string): Promise<string> {
        const prompt = `
You are a text correction assistant. ONLY correct or replace the city name in the given sentence based on common abbreviations or misspellings.
Rules:
- If no city abbreviation/misspelling is present, return the sentence unchanged.
- If a recognized abbreviation exists (e.g., "SN" → "Essen"), replace with the correct city.
- If the sentence contains a spoken/misspelled office location in Essen/Germany (from the list below), replace with the exact office name.
- Keep other words and punctuation identical.
- Do not add extra words unless needed for grammar.

Valid office names (Essen, Germany):
- Ruhr Tech
- Brüsseler Platz
- Lysegang 11
- Altenessener Straße 32
- Kettwig or Montebruchstraße 31
- Altenessener Straße 45
- Baumstr. 24
- Montebruchstr. 20
- Ernestinenstraße 60

Handle variants (e.g., "Brusseler Platz" → "Brüsseler Platz", "Montebruch street" → "Montebruchstr. 20", "Lysegang eleven" → "Lysegang 11").

Input: "${text}"
`.trim();

        const { axios, baseURL, jwtTokenFn, customisation } = aiConfig;
        const jwtToken = await jwtTokenFn();

        const axiosInstance = axios.create({
            baseURL,
            headers: {
                Authorization: `Bearer ${jwtToken}`,
                "Content-Type": "application/json",
            },
        });

        try {
            const response = await axiosInstance.post(
                `/service/completions`,
                {
                    messages: [
                        {
                            role: "system",
                            content: "You are a helpful assistant that corrects city names only.",
                        },
                        { role: "user", content: prompt },
                    ],
                    ...customisation,
                }
            );
            const corrected = response.data?.choices?.[0]?.message?.content;
            const cleaned = (corrected || text).trim().replace(/^["']|["']$/g, '');
            return cleaned;
        } catch (error) {
            console.error("[VA][fetchCorrectCity] error:", error);
            return text;
        }
    }

    // ------------------------------
    // Permissions overlay
    // ------------------------------
    if (micStatus !== "granted") {
        return (
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    zIndex: 1003,
                    background: "#000000bf",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "18px",
                    color: "#fd0101ff",
                    flexDirection: "column",
                }}
            >
                <span>
                    {recognitionLanguage.startsWith("de")
                        ? "Mikrofonberechtigung verweigert. Klicken Sie auf das Schlosssymbol in der Adressleiste Ihres Browsers, um den Zugriff zuzulassen."
                        : "Microphone permission denied. Click the lock icon in your browser's address bar to allow access."}
                </span>
            </div>
        );
    }

    return null; // headless component
};
